// 학생 벌점 현황 페이지

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useStudentAuth } from '../hooks/useStudentAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface PenaltyRecord {
  id: string;
  date: string;
  reason: string;
  points: number;
  point_type?: string;
  status: 'active' | 'resolved' | 'cancelled';
}

interface PenaltySummary {
  current_points?: number;
  total_points: number;
  total_demerit?: number;
  demerit_points?: number;
  total_merit?: number;
  merit_points?: number;
  active_count: number;
}

export default function PenaltyPage() {
  const { student } = useStudentAuth();
  const [records, setRecords] = useState<PenaltyRecord[]>([]);
  const [summary, setSummary] = useState<PenaltySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPenalties();
  }, [student]);

  const fetchPenalties = async () => {
    if (!student?.id) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('student_access_token');

      const response = await fetch(`${API_BASE_URL}/api/v1/penalty/student/${student.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRecords(data.records || []);
        setSummary(data.summary || null);
      }
    } catch (error) {
      console.error('벌점 조회 에러:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 현재 벌점(누적 벌점 - 상점), 상점/벌점 누적은 별도 표시
  const currentPoints = summary?.current_points ?? summary?.total_points ?? 0;
  const totalDemerit = summary?.demerit_points ?? summary?.total_demerit ?? 0;
  const totalMerit = summary?.merit_points ?? summary?.total_merit ?? 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        상벌점 현황
      </Typography>

      {/* 벌점 요약 */}
      <Card sx={{ mt: 2, mb: 3, backgroundColor: currentPoints > 0 ? '#fff3e0' : '#e8f5e9' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: currentPoints > 0 ? '#ff9800' : '#4caf50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WarningAmberIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                현재 벌점
              </Typography>
              <Typography variant="h3" fontWeight={700} color={currentPoints > 0 ? 'warning.main' : 'success.main'}>
                {currentPoints}점
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                벌점 누적 <b>{totalDemerit}점</b>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                상점 누적 <b style={{ color: '#16a34a' }}>{totalMerit}점</b>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {currentPoints >= 10 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          벌점 10점 이상 누적 시 이용에 제한이 있을 수 있습니다.
        </Alert>
      )}

      {/* 상벌점 내역 */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            상벌점 내역
          </Typography>

          {isLoading ? (
            <Typography>로딩 중...</Typography>
          ) : records.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                상벌점 내역이 없습니다. 좋습니다!
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>날짜</TableCell>
                    <TableCell>구분</TableCell>
                    <TableCell>사유</TableCell>
                    <TableCell align="center">점수</TableCell>
                    <TableCell>상태</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => {
                    // 상점(merit)/벌점(demerit)은 point_type 으로만 구분
                    const isMerit = record.point_type === 'merit';
                    return (
                      <TableRow key={record.id}>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>
                          <Chip
                            label={isMerit ? '상점' : '벌점'}
                            color={isMerit ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{record.reason}</TableCell>
                        <TableCell align="center">
                          <Typography
                            color={isMerit ? 'success.main' : 'error'}
                            fontWeight={600}
                          >
                            {isMerit ? '+' : '-'}{Math.abs(record.points)}점
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.status === 'active' ? '활성' : '해제'}
                            color={record.status === 'active' ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
